import { useState, useEffect, useCallback, useRef } from 'react';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../constants/analyticsEvents';
import useAnalytics from '../../../../hooks/useAnalytics';
import { search, SearchResultItem, SearchTab } from '../api/search-api';

interface UseSearchWithOffsetResult {
  results: SearchResultItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  loadMore: () => void;
}

interface SearchState {
  results: SearchResultItem[];
  offset: number | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
}

const initialState: SearchState = {
  results: [],
  offset: null,
  isLoading: false,
  isLoadingMore: false,
  error: null,
};

export function useSearchWithOffset(
  moduleId: string | undefined,
  term: string,
  tab: SearchTab,
): UseSearchWithOffsetResult {
  const [state, setState] = useState<SearchState>(initialState);
  const { capture } = useAnalytics();
  const lastLaunchedTermRef = useRef<string | null>(null);

  useEffect(() => {
    const trimmedTerm = term.trim();

    if (!moduleId || !trimmedTerm) {
      setState(initialState);
      lastLaunchedTermRef.current = null;
      return;
    }

    setState({ ...initialState, isLoading: true });

    const isNewTerm = lastLaunchedTermRef.current !== trimmedTerm;
    lastLaunchedTermRef.current = trimmedTerm;

    const searchPromise = tab === 'all' ? search(moduleId, trimmedTerm) : search(moduleId, trimmedTerm, undefined, tab);

    const makeSearchRequest = async () => {
      try {
        const { data, nextOffset, offset, queryTextEffective, resultsTotal, queryMs } = await searchPromise;
        setState(prev => ({ ...prev, results: data, offset: nextOffset, isLoading: false }));
        if (isNewTerm) {
          capture({
            eventType: ANALYTICS_EVENT_TYPES.SEARCH_LAUNCHED,
            objectId: moduleId,
            subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SEARCH,
            queryTextRaw: term,
            queryTextEffective,
            offset,
            resultsTotal,
            queryMs,
          });
        }
      } catch (err) {
        setState(prev => ({ ...prev, error: err as Error, isLoading: false }));
      }
    };

    makeSearchRequest();
  }, [moduleId, term, tab, capture]);

  const loadMore = useCallback(() => {
    if (!state.offset || state.isLoadingMore || !moduleId) return;

    const currentOffset = state.offset;
    setState(prev => ({ ...prev, isLoadingMore: true, error: null }));

    const searchPromise =
      tab === 'all' ? search(moduleId, term.trim(), currentOffset) : search(moduleId, term.trim(), currentOffset, tab);

    const makeLoadMoreRequest = async () => {
      try {
        const { data, nextOffset, offset, queryTextEffective, resultsTotal, queryMs } = await searchPromise;
        setState(prev => ({ ...prev, results: [...prev.results, ...data], offset: nextOffset, isLoadingMore: false }));
        capture({
          eventType: ANALYTICS_EVENT_TYPES.SEARCH_LAUNCHED,
          objectId: moduleId,
          subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SEARCH,
          queryTextRaw: term,
          queryTextEffective,
          offset,
          resultsTotal,
          queryMs,
        });
      } catch (err) {
        setState(prev => ({ ...prev, error: err as Error, isLoadingMore: false }));
      }
    };

    makeLoadMoreRequest();
  }, [state.offset, state.isLoadingMore, moduleId, tab, term, capture]);

  return {
    results: state.results,
    isLoading: state.isLoading,
    isLoadingMore: state.isLoadingMore,
    hasMore: state.offset !== null,
    error: state.error,
    loadMore,
  };
}
